import {
    Button,
    Intent,
    OverlayToaster,
    Position,
    Tooltip
} from "@blueprintjs/core";
import { segmentTrackDataFreshnessDownloadQueueProjects } from "analytics/analyticTrackEvents";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { useGetQueueProjectsLazyQuery } from "generated/graphql";
import { convertHasuraQueueProjectsToNiraQueueProjects } from "types/queueProjectsType";
import { getScopeFromScopes } from "types/scope";
import { downloadJSONToCSV } from "utils/csv";
import { queueProjectsToExportObject } from "utils/queueProjectUtils";
import { getQueueProjectsExportColumnsForRegion } from "./queueProjectsExportColumn";

const DownloadQueueProjectsToaster = OverlayToaster.create({
    position: Position.TOP
});

const DownloadQueueProjectsButton: React.FC = () => {
    const { region, scopeView } = useMapViewRoutingMetadata();
    const {
        busFiltersConfiguration: {
            busFilters: { scope }
        }
    } = useMapViewConfiguration();
    const user = useUserDataContext();

    const [getQueueProjects] = useGetQueueProjectsLazyQuery();

    // this should never actually be undefined since otherwise the user wouldn't be able to select it
    const scopeLabel = getScopeFromScopes(user.scopes, scope)?.display_name;

    const queueProjectsExportColumns =
        getQueueProjectsExportColumnsForRegion(region);

    const handleDownloadQueueProjects = async () => {
        const data = (
            await getQueueProjects({
                variables: { scope, region, scope_views: [scopeView] }
            })
        ).data;

        if (data === undefined) {
            DownloadQueueProjectsToaster.show({
                message:
                    "Encountered an error while downloading queue projects.",
                intent: Intent.DANGER
            });
            return;
        }

        const queueProjects =
            convertHasuraQueueProjectsToNiraQueueProjects(data);
        const exportObjects = queueProjectsToExportObject(
            queueProjects,
            queueProjectsExportColumns
        );
        await downloadJSONToCSV({
            json: exportObjects,
            filename: `Nira Queue Projects - ${region} - ${scopeLabel}.csv`
        });

        DownloadQueueProjectsToaster.show({
            message: "Downloaded queue projects.",
            intent: Intent.SUCCESS
        });
        segmentTrackDataFreshnessDownloadQueueProjects(
            region,
            scope,
            scopeView
        );
    };

    return (
        <Tooltip
            content={
                <div>
                    Click to download a CSV of which projects <br /> were
                    included in this scope.
                </div>
            }
            position={Position.TOP_RIGHT}
        >
            <Button
                text="Download Projects"
                onClick={handleDownloadQueueProjects}
            />
        </Tooltip>
    );
};

export default DownloadQueueProjectsButton;
